import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* Offizielle, unveränderte deutsche Badges (Anbieter-eigene Assets, keine Nachbauten). */
const GOOGLE_BADGE = "/wallet/google-wallet-de.svg";
/* Apple-Badge: offizielle deutsche SVG von Apple (developer.apple.com, Wallet-Marketing-Lizenz).
   Datei unter /public/wallet/apple-wallet-de.svg. Fehlt sie, blendet sich der Apple-Button per
   onError automatisch aus – es wird NIE ein inoffizieller Ersatz gezeigt (Apple-Markenvorgaben). */
const APPLE_BADGE = "/wallet/apple-wallet-de.svg";

let _statusCache = null;
let _statusPromise = null;

export function useWalletStatus() {
  const [status, setStatus] = useState(_statusCache);
  useEffect(() => {
    let alive = true;
    if (_statusCache) { setStatus(_statusCache); return; }
    if (!_statusPromise) {
      _statusPromise = fetch(`${BACKEND}/api/wallet/status`)
        .then((r) => (r.ok ? r.json() : null))
        .then((s) => { if (s) _statusCache = s; return s; })
        .catch(() => null);
    }
    _statusPromise.then((s) => { if (alive && s) setStatus(s); });
    return () => { alive = false; };
  }, []);
  return status || {};
}

/* Reinit nach Admin-Änderung (Flag an/aus): Cache leeren, damit öffentliche Seiten neu laden. */
export function resetWalletStatus() { _statusCache = null; _statusPromise = null; _appleBadge = null; _appleBadgePromise = null; }

/* Einmalige, gecachte Prüfung, ob das offizielle Apple-SVG wirklich vorliegt (nicht der SPA-HTML-
   Fallback). Verhindert ein kurzes "kaputtes Bild" und blendet den Apple-Button + den Apple-
   Markenhinweis konsistent aus, solange Thomas das Original noch nicht hochgeladen hat. */
let _appleBadge = null;
let _appleBadgePromise = null;

function useAppleBadgeReady() {
  const [ok, setOk] = useState(_appleBadge === true);
  useEffect(() => {
    let alive = true;
    if (_appleBadge !== null) { setOk(_appleBadge); return; }
    if (!_appleBadgePromise) {
      _appleBadgePromise = fetch(APPLE_BADGE, { method: "GET", cache: "force-cache" })
        .then((r) => r.ok && (r.headers.get("content-type") || "").toLowerCase().includes("svg"))
        .catch(() => false)
        .then((v) => { _appleBadge = !!v; return _appleBadge; });
    }
    _appleBadgePromise.then((v) => { if (alive) setOk(v); });
    return () => { alive = false; };
  }, []);
  return ok;
}

/**
 * Geräte-bewusste Wallet-Buttons. Öffentlich nur sichtbar, wenn wallet_live aktiv ist.
 * - googleEndpoint: API-Pfad (relativ zu Backend), liefert { save_url } → Google Wallet
 * - appleHref: direkter .pkpass-Download (Apple Wallet)
 * - credentials: true, wenn Session-Cookie mitgeschickt werden muss (Kundenkarte)
 */
export function WalletButtons({ googleEndpoint, appleHref, credentials = false, className = "", compact = false, kind = "card", title = "" }) {
  const { wallet_live, apple_ready, google_ready } = useWalletStatus();
  const appleBadgeReady = useAppleBadgeReady();
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const goBranded = useCallback((p, url) => {
    const q = new URLSearchParams({ p, kind });
    q.set(p === "apple" ? "a" : "g", url);
    if (title) q.set("title", title);
    navigate(`/wallet/hinzufuegen?${q.toString()}`);
  }, [navigate, kind, title]);

  const openGoogle = useCallback(async () => {
    if (!googleEndpoint) return;
    setBusy(true);
    try {
      const opts = credentials ? { credentials: "include", headers: { "X-Requested-With": "XMLHttpRequest" } } : {};
      const r = await fetch(`${BACKEND}${googleEndpoint}`, opts);
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.save_url) throw new Error(j.detail || "Fehler");
      goBranded("google", j.save_url);
    } catch {
      toast.error("Google Wallet konnte gerade nicht geöffnet werden. Bitte erneut versuchen.");
    } finally { setBusy(false); }
  }, [googleEndpoint, credentials, goBranded]);

  const openApple = useCallback((e) => { e.preventDefault(); goBranded("apple", appleHref); }, [goBranded, appleHref]);

  /* Auch das rohe href zeigt auf die gebrandete Zwischenseite /wallet/hinzufuegen (nie direkt aufs
     .pkpass) – so läuft jeder Apple-Klick über die DIMEDIAL-Seite, selbst ohne JavaScript. */
  const brandedAppleHref = useMemo(() => {
    if (!appleHref) return undefined;
    const q = new URLSearchParams({ p: "apple", kind });
    q.set("a", appleHref);
    if (title) q.set("title", title);
    return `/wallet/hinzufuegen?${q.toString()}`;
  }, [appleHref, kind, title]);

  const showGoogle = google_ready && !!googleEndpoint;
  const showApple = apple_ready && appleBadgeReady && !!appleHref;
  if (!wallet_live || (!showGoogle && !showApple)) return null;

  const bothShown = showGoogle && showApple;
  // Beide Badges stehen als gleich breite Spalten nebeneinander und skalieren auf schmalen
  // Handys mit (seitenverhältnistreu, offizielle Assets unverändert) herunter.
  const cell = bothShown
    ? "flex-1 basis-0 min-w-[132px] max-w-[220px] flex items-center justify-center"
    : "inline-flex items-center justify-center";
  // Gleiche feste Höhe für beide offiziellen Badges (seitenverhältnistreu, Assets unverändert).
  // Nötig, weil die Original-SVGs unterschiedliche Eigenhöhen haben (Google 55px, Apple 34,5px) –
  // sonst wirkt Google größer. Feste Höhe skaliert beide identisch; Breite ergibt sich aus dem Verhältnis.
  const img = "block w-auto h-[44px]";
  return (
    <div className={`flex flex-wrap items-stretch justify-center gap-2.5 ${className}`} data-testid="wallet-buttons">
      {showGoogle && (
        <button type="button" onClick={openGoogle} disabled={busy} aria-label="Zu Google Wallet hinzufügen"
          className={`${cell} transition-transform hover:-translate-y-px active:translate-y-0 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FDD105] rounded-[27px]`}
          data-testid="wallet-google-btn">
          {busy ? <span className="inline-flex items-center justify-center gap-2 w-full rounded-[27px] bg-[#1F1F1F] text-white px-4 text-sm font-medium h-[44px]"><Loader2 className="w-4 h-4 animate-spin" /> Wird geöffnet…</span>
                : <img src={GOOGLE_BADGE} alt="Zu Google Wallet hinzufügen" className={img} />}
        </button>
      )}
      {showApple && (
        <a href={brandedAppleHref} onClick={openApple} className={`${cell} transition-transform hover:-translate-y-px active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FDD105] rounded-[6px]`} data-testid="wallet-apple-btn" aria-label="Zu Apple Wallet hinzufügen">
          {/* Offizielles, unverändertes Apple-Badge; nur sichtbar, wenn die Datei real vorliegt. */}
          <img src={APPLE_BADGE} alt="Zu Apple Wallet hinzufügen" className={img} />
        </a>
      )}
    </div>
  );
}

/**
 * Werbliche Wallet-Vertrauensleiste für Verkaufsflächen (Startseite, Footer, /preise, /kundenkarte,
 * /tickets, Anmeldung …). Zeigt den Satz + beide offiziellen Badges als ECHTE Buttons:
 * Wo kein konkreter Pass existiert, fügen die Badges eine DIMEDIAL-Demo-Kundenkarte bzw. ein
 * Demo-Ticket hinzu (Apple-Lizenz: Badge nur zusammen mit eigenem Pass). Öffentlich nur bei wallet_live.
 */
export function WalletTrustBar({ text = "Kundenkarten & Tickets in Apple Wallet & Google Wallet", demo = "card", className = "", testId = "wallet-trust-bar", stacked = false }) {
  const { wallet_live, apple_ready, google_ready } = useWalletStatus();
  const appleBadgeReady = useAppleBadgeReady();
  if (!wallet_live || (!google_ready && !apple_ready)) return null;
  // Rein darstellende Vertrauens-Badges auf Verkaufs-/Marketingseiten: offizielle Assets sichtbar,
  // aber NICHT klickbar und OHNE Demokarten-/Demo-Ticket-Download (Entscheidung Thomas).
  const showApple = apple_ready && appleBadgeReady;
  const badgeImg = "block w-auto h-[40px] select-none";
  return (
    <div className={`rounded-2xl border border-black/10 bg-white/95 px-4 py-3.5 shadow-[0_1px_0_rgba(0,0,0,0.04)] ${className}`} data-testid={testId}>
      <div className={`flex flex-col gap-3 ${stacked ? "" : "sm:flex-row sm:items-center sm:justify-between"}`}>
        <p className="flex items-center gap-2 text-sm font-semibold text-[#111]">
          <Wallet className="h-4 w-4 shrink-0 text-[#111]" />{text}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2.5 shrink-0 pointer-events-none" data-testid="wallet-trust-badges" aria-hidden="true">
          {google_ready && <img src={GOOGLE_BADGE} alt="Google Wallet" className={badgeImg} draggable="false" />}
          {showApple && <img src={APPLE_BADGE} alt="Apple Wallet" className={badgeImg} draggable="false" />}
        </div>
      </div>
      {apple_ready && appleBadgeReady && (
        <p className="mt-2.5 text-[10px] leading-snug text-black/45" data-testid="wallet-apple-credit">
          Apple, Apple&nbsp;Watch, iPhone und iPod&nbsp;touch sind Marken der Apple&nbsp;Inc., eingetragen in den USA und anderen Ländern.
        </p>
      )}
    </div>
  );
}
